import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { API } from "../App";
import { toast } from "sonner";
import axios from "axios";
import { ArrowLeft, Check, CheckCircle, Copy, Mail, Camera, User, Phone, MessageCircle, QrCode, Smartphone, Wallet } from "lucide-react";

const LOGO_URL = "/achraj_logo.png";

const PLAN_CREDITS = { D: 500, C: 1000, B: 2000, A: 4000 };

const PLANS = [
  { value: "D", label: "Registration Plan", levels: 17, desc: "100 PV" },
];

export default function RegisterPage() {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    plan_type: "D",
    sponsor_id: searchParams.get("ref") || "",
    address: "",
    nominee_name: "",
    nominee_relation: "",
    photo_url: ""
  });
  const [loading, setLoading] = useState(false);
  const [registrationResult, setRegistrationResult] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [upiData, setUpiData] = useState(null);
  const navigate = useNavigate();

  const planAmount = PLAN_CREDITS[formData.plan_type] || 0;

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // For now, we'll use a data URL for preview
      // In production, you'd upload to a storage service
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
        // For demo, we'll store the data URL (in production, upload to cloud storage)
        setFormData({ ...formData, photo_url: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  // Submit FREE registration — immediately creates an active member with a serial Member ID.
  const submitRegistration = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || formData.phone.length < 10 || !formData.sponsor_id) {
      toast.error("Please fill all required fields correctly.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/register`, { ...formData }, { timeout: 30000 });
      setRegistrationResult(res.data);
      toast.success(`Registration successful! Your Member ID is ${res.data.member_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed. Please try again.", { duration: 6000 });
    } finally {
      setLoading(false);
    }
  };

  const copyCredentials = () => {
    const text = `Member ID: ${registrationResult.member_id}\nPassword: ${registrationResult.password}`;
    navigator.clipboard.writeText(text);
    toast.success("Credentials copied!");
  };

  // Registration success screen — member created instantly (free, serial Member ID)
  if (registrationResult) {
    const plan = registrationResult.plan_type || formData.plan_type;
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-3">
        <Card className="max-w-md w-full border-0 shadow-xl" data-testid="registration-success-card">
          <CardHeader className="text-center pb-3 pt-6">
            <div className="w-14 h-14 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg shadow-green-500/30">
              <CheckCircle className="w-7 h-7 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold text-slate-800">Registration Successful!</CardTitle>
            <CardDescription className="text-sm text-slate-600 mt-1 leading-snug">
              आपका Achraj परिवार में स्वागत है! आपकी Member ID बन गई है।
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 pb-6">
            <div className="bg-slate-100 rounded-xl p-4 space-y-3">
              <div className="flex justify-between items-start gap-3">
                <span className="text-slate-600 text-sm pt-0.5">Name</span>
                <span className="font-bold text-base text-slate-800 text-right" data-testid="success-member-name">{registrationResult.name || formData.name}</span>
              </div>
              <div className="flex justify-between items-center gap-3 border-t border-slate-200 pt-3">
                <span className="text-slate-600 text-sm">Member ID</span>
                <span className="font-bold text-lg text-[#D4AF37]" data-testid="success-member-id">{registrationResult.member_id}</span>
              </div>
              <div className="flex justify-between items-center gap-3 border-t border-slate-200 pt-3">
                <span className="text-slate-600 text-sm">Password</span>
                <span className="font-bold text-base text-slate-800" data-testid="success-password">{registrationResult.password}</span>
              </div>
              <div className="flex justify-between items-center gap-3 border-t border-slate-200 pt-3">
                <span className="text-slate-600 text-sm">Sponsor ID</span>
                <span className="font-bold text-base text-[#E65100]">{registrationResult.sponsor_id || formData.sponsor_id}</span>
              </div>
              <div className="flex justify-between items-center gap-3 border-t border-slate-200 pt-3">
                <span className="text-slate-600 text-sm">Plan</span>
                <span className="font-semibold text-sm text-slate-800 text-right">Plan {plan}</span>
              </div>
            </div>

            <p className="text-xs text-center text-slate-500">
              Your login password is the last 4 digits of your phone number. Please save your Member ID.
            </p>

            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" onClick={copyCredentials} className="w-full" data-testid="copy-credentials-btn">
                Copy Login Details
              </Button>
              <Link to="/login" className="block">
                <Button className="w-full bg-[#E65100] hover:bg-[#D84315] text-white" data-testid="go-to-login-btn">
                  Login Now
                </Button>
              </Link>
            </div>

            <Link to="/" className="block">
              <Button variant="ghost" className="w-full" data-testid="back-to-home-btn">
                Back to Home
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      {/* Left Side - Indian Business Professionals */}
      <div className="hidden md:block relative">
        <img 
          src="https://static.prod-images.emergentagent.com/jobs/93e9d039-575d-404f-8637-fb4316305502/images/6c37cb583ad3de6ea63da5d7def378c15a17582feeb63f060effa98be847c887.png" 
          alt="Indian Business Professionals"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/40 to-[#E65100]/50"></div>
        <div className="absolute inset-0 flex flex-col justify-center p-12">
          <Link to="/" className="flex items-center gap-3 mb-8">
            <img src={LOGO_URL} alt="Achraj Logo" className="h-16 w-16 rounded-full object-cover border-2 border-white/50 shadow-lg" />
          </Link>
          <h1 className="text-4xl font-bold text-white mb-2">Join the Achraj Family</h1>
          <p className="text-[#D4AF37] text-xl font-semibold mb-4">जुडकर खर्च नहीं, आय करें</p>
          <p className="text-white/90 text-lg mb-8">
            Start your journey to financial freedom with our 20-level income plan. <span className="font-semibold text-[#D4AF37]">Quality products, happy families!</span>
          </p>
          <div className="space-y-4">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <p className="text-white font-semibold">👨‍👩‍👧‍👦 Family Business</p>
              <p className="text-white/80 text-sm">Build a legacy with your loved ones</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <p className="text-white font-semibold">💰 Monthly Income System</p>
              <p className="text-white/80 text-sm">Earn 2% per level from your AI network</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
              <p className="text-white font-semibold">🏆 Growth & Recognition</p>
              <p className="text-white/80 text-sm">Achieve new ranks, unlock rewards</p>
            </div>
          </div>
          <div className="mt-6 flex items-center gap-2 text-white">
            <Mail className="w-5 h-5 text-[#D4AF37]" />
            <a href="mailto:achrajaigroup@gmail.com" className="font-semibold hover:underline">achrajaigroup@gmail.com</a>
            <span className="text-white/60">| 24/7 Support</span>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex flex-col justify-center p-8 md:p-12 bg-slate-50 overflow-y-auto">
        <div className="md:hidden mb-8">
          <Link to="/" className="inline-flex items-center text-slate-600 hover:text-slate-900">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
          </Link>
        </div>

        <div className="max-w-md mx-auto w-full">
          <Card className="border-0 shadow-xl">
            <CardHeader className="text-center">
              <div className="md:hidden flex justify-center mb-4">
                <img src={LOGO_URL} alt="Achraj Logo" className="h-14 w-14 rounded-full object-cover border-2 border-[#E65100] shadow-md" />
              </div>
              <CardTitle className="text-2xl font-bold">Join Achraj</CardTitle>
              <CardDescription>Register as a member</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={submitRegistration} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="sponsor">Sponsor ID (Required)</Label>
                  <Input
                    id="sponsor"
                    type="text"
                    placeholder="ACJ00001"
                    data-testid="register-sponsor-input"
                    value={formData.sponsor_id}
                    onChange={(e) => setFormData({ ...formData, sponsor_id: e.target.value.toUpperCase() })}
                    required
                    className="h-12"
                  />
                  <p className="text-xs text-slate-500">Enter your sponsor's Member ID</p>
                </div>

                {/* Photo Upload Section */}
                <div className="space-y-2">
                  <Label>Your Photo</Label>
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      {photoPreview ? (
                        <img 
                          src={photoPreview} 
                          alt="Preview" 
                          className="w-20 h-20 rounded-full object-cover border-2 border-[#E65100]"
                        />
                      ) : (
                        <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center border-2 border-dashed border-slate-300">
                          <User className="w-8 h-8 text-slate-400" />
                        </div>
                      )}
                      <label 
                        htmlFor="photo-upload" 
                        className="absolute bottom-0 right-0 w-7 h-7 bg-[#E65100] rounded-full flex items-center justify-center cursor-pointer hover:bg-[#D84315] transition-colors"
                      >
                        <Camera className="w-4 h-4 text-white" />
                      </label>
                      <input
                        id="photo-upload"
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoChange}
                        className="hidden"
                      />
                    </div>
                    <div className="text-sm text-slate-500">
                      <p>Upload your passport-size photo</p>
                      <p className="text-xs text-slate-400">JPG, PNG (Max 2MB)</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Your full name"
                    data-testid="register-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="h-12"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    data-testid="register-email-input"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="h-12"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Mobile Number</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="9876543210"
                    data-testid="register-phone-input"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '') })}
                    required
                    minLength={10}
                    maxLength={10}
                    className="h-12"
                  />
                  <p className="text-xs text-slate-500">Last 4 digits will be your password</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="plan">Plan</Label>
                  <Select
                    value={formData.plan_type}
                    onValueChange={(value) => setFormData({ ...formData, plan_type: value })}
                  >
                    <SelectTrigger data-testid="register-plan-select" className="h-12">
                      <SelectValue placeholder="Select a plan" />
                    </SelectTrigger>
                    <SelectContent>
                      {PLANS.map((plan) => (
                        <SelectItem key={plan.value} value={plan.value}>
                          <div>
                            <span className="font-medium">{plan.label}</span>
                            <span className="text-slate-500 text-sm ml-2">({plan.desc})</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address">Address</Label>
                  <Input
                    id="address"
                    type="text"
                    placeholder="Your address"
                    data-testid="register-address-input"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="h-12"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="nominee">Nominee Name</Label>
                    <Input
                      id="nominee"
                      type="text"
                      placeholder="Nominee name"
                      value={formData.nominee_name}
                      onChange={(e) => setFormData({ ...formData, nominee_name: e.target.value })}
                      className="h-10"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="relation">Relation</Label>
                    <Input
                      id="relation"
                      type="text"
                      placeholder="Relation"
                      value={formData.nominee_relation}
                      onChange={(e) => setFormData({ ...formData, nominee_relation: e.target.value })}
                      className="h-10"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  data-testid="register-submit-btn"
                  disabled={loading}
                  className="w-full h-12 bg-gradient-to-r from-[#E65100] to-[#FF8F00] hover:from-[#D84315] hover:to-[#E65100] text-white font-semibold rounded-full"
                >
                  {loading ? "Registering..." : "Register Free"}
                </Button>
              </form>

              <div className="mt-6 text-center text-sm text-slate-600">
                Already a member?{" "}
                <Link to="/login" className="text-[#E65100] font-semibold hover:underline">
                  Sign In
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
